import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Card,
  Button,
  Form,
  Nav,
  Tab,
  Container,
  Row,
  Col,
  Spinner,
  Alert,
} from 'react-bootstrap'
import TmsHeader from '../TmsComponents/TmsHeader'
import TmsSidebar from '../TmsComponents/TmsSidebar'

export default function AddParticipants() {
  const [tabValue, setTabValue] = useState('bulk')
  const [selectedEvent, setSelectedEvent] = useState('')
  const [file, setFile] = useState(null)
  const [participant, setParticipant] = useState({
    name: '',
    email: '',
    mobile: '',
  })
  const [trainings, setTrainings] = useState([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ text: '', variant: '' })
  const [error, setError] = useState('')
  const URL = 'https://kaasbackend.vetrinahealthcare.com/'

  useEffect(() => {
    const fetchTrainings = async () => {
      const token = localStorage.getItem('jwttoken')
      try {
        const response = await axios.get(`${URL}tms/training/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        setTrainings(response.data.trainings)
      } catch (error) {
        setMessage({
          text: 'Error fetching training events.',
          variant: 'danger',
        })
      }
    }

    fetchTrainings()
  }, [])

  const handleTabChange = (eventKey) => setTabValue(eventKey)

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    const allowedFormats = ['text/csv']

    if (selectedFile && allowedFormats.includes(selectedFile.type)) {
      setFile(selectedFile)
      setError('')
    } else {
      setFile(null)
      setError('Please upload a valid CSV file.')
    }
  }

  const handleParticipantChange = (e) => {
    const { name, value } = e.target
    setParticipant((prev) => ({ ...prev, [name]: value }))

    if (name === 'mobile_number') {
      const mobilePattern = /^[0-9]{10}$/
      setError(
        mobilePattern.test(value)
          ? ''
          : 'Please enter a valid 10-digit mobile number'
      )
    }
  }

  const handleBulkUpload = async () => {
    if (!file || !selectedEvent) {
      setMessage({
        text: 'Please select an event and upload a file.',
        variant: 'warning',
      })
      return
    }

    setLoading(true)
    const formData = new FormData()
    formData.append('event', selectedEvent)
    formData.append('file', file)

    try {
      const token = localStorage.getItem('jwttoken')
      await axios.post(
        `${URL}tms/training/${selectedEvent}/bulk-enrollment/`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      setMessage({ text: 'Bulk upload successful!', variant: 'success' })
      setFile(null)
    } catch (error) {
      setMessage({ text: 'Error during bulk upload.', variant: 'danger' })
    } finally {
      setLoading(false)
    }
  }

  const handleManualAdd = async () => {
    const token = localStorage.getItem('jwttoken')
    if (
      !selectedEvent ||
      !participant.name ||
      !participant.email ||
      !participant.mobile_number
    ) {
      setMessage({ text: 'Please fill in all fields.', variant: 'warning' })
      return
    }

    setLoading(true)
    try {
      await axios.post(
        `${URL}tms/training/${selectedEvent}/enrollments/`,
        participant,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      setMessage({
        text: 'Participant added successfully!',
        variant: 'success',
      })
      setParticipant({ name: '', email: '', mobile: '' })
    } catch (error) {
      setMessage({ text: 'Error adding participant.', variant: 'danger' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#f0f4f8',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <TmsSidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <TmsHeader />
        <Container fluid className='py-4'>
          <Row className='justify-content-center'>
            <Col lg={8}>
              {message.text && (
                <Alert
                  variant={message.variant}
                  onClose={() => setMessage({ text: '', variant: '' })}
                  dismissible
                >
                  {message.text}
                </Alert>
              )}
              <Card
                className='shadow p-4 rounded'
                style={{ backgroundColor: '#f8f9fa' }}
              >
                <Card.Header className='text-center text-primary'>
                  Add Participants
                </Card.Header>
                <Card.Body>
                  <Tab.Container
                    activeKey={tabValue}
                    onSelect={handleTabChange}
                  >
                    <Nav variant='tabs' className='justify-content-center mb-4'>
                      <Nav.Item>
                        <Nav.Link eventKey='bulk'>Bulk Upload</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey='manual'>Add Manually</Nav.Link>
                      </Nav.Item>
                    </Nav>
                    <Tab.Content>
                      <Tab.Pane eventKey='bulk'>
                        <Form.Group className='mb-3'>
                          <Form.Label>Select Training Event</Form.Label>
                          <Form.Select
                            value={selectedEvent}
                            onChange={(e) => setSelectedEvent(e.target.value)}
                          >
                            <option value=''>Select training event</option>
                            {trainings.map((training) => (
                              <option
                                key={training.training_id}
                                value={training.training_id}
                              >
                                {training.title}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className='mb-3'>
                          <Form.Label>Upload CSV File</Form.Label>
                          <Form.Control
                            type='file'
                            onChange={handleFileChange}
                            accept='.csv'
                          />
                        </Form.Group>

                        {error && <Alert variant='danger'>{error}</Alert>}

                        <Button
                          variant='primary'
                          onClick={handleBulkUpload}
                          disabled={!file || loading}
                          className='w-100 d-flex align-items-center justify-content-center'
                        >
                          {loading ? (
                            <Spinner as='span' animation='border' size='sm' />
                          ) : (
                            'Upload'
                          )}
                        </Button>
                      </Tab.Pane>

                      <Tab.Pane eventKey='manual'>
                        <Form.Group className='mb-3'>
                          <Form.Label>Select Training Event</Form.Label>
                          <Form.Select
                            value={selectedEvent}
                            onChange={(e) => setSelectedEvent(e.target.value)}
                          >
                            <option value=''>Select training event</option>
                            {trainings.map((training) => (
                              <option
                                key={training.training_id}
                                value={training.training_id}
                              >
                                {training.title}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className='mb-3'>
                          <Form.Label>Name</Form.Label>
                          <Form.Control
                            type='text'
                            placeholder='Enter participant name'
                            name='name'
                            value={participant.name}
                            onChange={handleParticipantChange}
                          />
                        </Form.Group>

                        <Form.Group className='mb-3'>
                          <Form.Label>Email ID</Form.Label>
                          <Form.Control
                            type='email'
                            placeholder='Enter email address'
                            name='email'
                            value={participant.email}
                            onChange={handleParticipantChange}
                          />
                        </Form.Group>

                        <Form.Group className='mb-3'>
                          <Form.Label>Mobile Number</Form.Label>
                          <Form.Control
                            type='tel'
                            placeholder='Enter mobile number'
                            name='mobile_number' // Updated here
                            value={participant.mobile_number} // Updated here
                            onChange={handleParticipantChange}
                            isInvalid={!!error}
                          />
                          {error && (
                            <Form.Control.Feedback type='invalid'>
                              {error}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>

                        <Button
                          variant='primary'
                          onClick={handleManualAdd}
                          disabled={loading}
                          className='w-100 d-flex align-items-center justify-content-center'
                        >
                          {loading ? (
                            <Spinner as='span' animation='border' size='sm' />
                          ) : (
                            'Add Participant'
                          )}
                        </Button>
                      </Tab.Pane>
                    </Tab.Content>
                  </Tab.Container>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  )
}
